import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { RootState } from '../../app/store';
import { ConnectedProps, connect } from 'react-redux';
import { useIntl } from 'react-intl';
import messages from './messages';
import { Menu } from '@material-ui/core';
import { StyledMenuItem } from '../../components/Button/styles';
import {
  accentColorPrim,
  accentColorSecond,
  fontColorPrim,
  fontColorSecond,
  green,
  red,
  white,
} from '../../assets/styledAssets/vars';
import { StyledFormField } from '../../components/FormField/styles';
import { Input } from '@progress/kendo-react-inputs';
import { SearchIcon } from '../../components/Icons/Icons.component';
import Button from '../../components/Button/Button.component';
import { GridColumn } from '@progress/kendo-react-grid';
import Moment from 'react-moment';
import debounce from 'lodash/debounce';
import { fetchTests } from './SiteOwnerTestResults.actions';
import {
  setSearch,
  toggleCheckTest,
  setListTests,
} from './SiteOwnerTestResults.slice';
import { StyledTable, Wrap } from '../../assets/styledAssets/grid';
import { deleteTest, toggleTestValidity } from '../../service/Results.service';
import { TestResults } from '../../domains/results';

const connector = connect(
  (state: RootState) => ({
    listTests: state.siteownertestresults.listTests,
    search: state.siteownertestresults.search,
    isFetching: state.siteownertestresults.isFetching,
    total: state.siteownertestresults.total,
    pageSize: state.siteownertestresults.pageSize,
  }),
  {
    fetchTests,
    setSearch,
    toggleCheckTest,
    setListTests,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const SiteOwnerTestResults: FunctionComponent<PropsFromRedux> = ({
  listTests,
  search,
  isFetching,
  total,
  pageSize,
  fetchTests,
  setSearch,
  toggleCheckTest,
  setListTests,
}) => {
  const intl = useIntl();
  const [skip, setSkip] = useState<number>(0);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  useEffect(() => {
    fetchTests(true);
    // eslint-disable-next-line
  }, []);

  const fetch = useMemo(
    () =>
      debounce(() => {
        fetchTests(true);
      }, 200),
    // eslint-disable-next-line
    [],
  );

  const handleChangeInput = (e: any) => {
    setSearch(e.target.value);
    fetch();
  };

  const pageHandler = useCallback(
    (event: any) => {
      setSkip(event.page.skip);
      if (
        skip + pageSize + 3 >= listTests.length &&
        !isFetching &&
        total > listTests.length
      ) {
        fetchTests(false);
      }
    },
    [isFetching, listTests.length, total, fetchTests, setSkip, skip, pageSize],
  );

  const deleteTests = () => {
    const selected = listTests.filter((f) => f.checked);
    const listRequests: Promise<TestResults>[] = [];
    selected.forEach((test) => {
      listRequests.push(deleteTest(test.id));
    });
    Promise.all(listRequests).then(() => {
      const newTests = listTests.filter(
        (t) => !selected.map((test) => test.id).includes(t.id),
      );
      setListTests(newTests);
    });

    handleClose();
  };

  const invalidateTests = () => {
    const selected = listTests.filter((f) => f.checked && !f.invalid);
    const listRequests: Promise<TestResults>[] = [];
    selected.forEach((test) => {
      listRequests.push(toggleTestValidity(test.id));
    });
    Promise.all(listRequests).then(() => {
      const newTests = listTests.map((t) => {
        if (selected.map((test) => test.id).includes(t.id)) {
          return { ...t, invalid: true };
        }
        return t;
      });
      setListTests(newTests);
    });
    handleClose();
  };

  const validateTests = () => {
    const selected = listTests.filter((f) => f.checked && f.invalid);
    const listRequests: Promise<TestResults>[] = [];
    selected.forEach((test) => {
      listRequests.push(toggleTestValidity(test.id));
    });
    Promise.all(listRequests).then(() => {
      const newTests = listTests.map((t) => {
        if (selected.map((test) => test.id).includes(t.id)) {
          return { ...t, invalid: false };
        }
        return t;
      });
      setListTests(newTests);
    });
    handleClose();
  };

  return (
    <>
      <Wrap
        width={'100%'}
        flexDir={'column'}
        flexWrap={'nowrap'}
        overflow={['auto', 'unset']}
      >
        <Wrap alignItems={['flex-start', 'center']} justifyCont={'space-between'} width={'100%'}>
          <Wrap alignItems={['flex-start', 'flex-end']} flexDir={['column', 'row']} width={'100%'}>
            <Wrap
              fs={'3.2rem'}
              fw={'bold'}
              color={'#000'}
              alignItems={'flex-end'}
              borders={[[{ side: 'right', color: 'transparent' }], [{side: 'right', color: accentColorSecond}]]}
              padding={['0', '0 3rem 0 0']}
              margin={['0 0 3rem 0', '0 3rem 0 0']}
            >
              {intl.formatMessage(messages.header)}
            </Wrap>
            <Wrap
              fs={'1.8rem'}
              color={fontColorSecond}
              alignItems={'flex-end'}
              padding={'0 0 .5rem 0'}
            >
              {total} {intl.formatMessage(messages.total)}
            </Wrap>
          </Wrap>
        </Wrap>
        <Wrap
          alignItems={['flex-start', 'center']}
          margin={['2rem 0', '4rem 0 3rem 0']}
          width={'100%'}
          justifyCont={['flex-start', 'space-between']}
          flexDir={['column', 'row']}
        >
          <Wrap width={'100%'} maxWidth={'34rem'}>
            <StyledFormField>
              <Input value={search} onChange={handleChangeInput} />
              <SearchIcon color={fontColorPrim} width={'2rem'} />
            </StyledFormField>
          </Wrap>
          <Wrap margin={['2rem 0 0 0', '0 0 0 1.6rem']}>
            <Button
              primary
              onClick={handleClick}
              aria-controls="simple-menu"
              aria-haspopup="true"
            >
              {intl.formatMessage(messages.actions)}
            </Button>
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <StyledMenuItem onClick={validateTests}>
                <Wrap fs={'1.4rem'} fw={'bold'} padding={'.5rem 1rem'}>
                  {intl.formatMessage(messages.markAsValid)}
                </Wrap>
              </StyledMenuItem>
              <StyledMenuItem onClick={invalidateTests}>
                <Wrap
                  fs={'1.4rem'}
                  fw={'bold'}
                  color={accentColorPrim}
                  padding={'.5rem 1rem'}
                >
                  {intl.formatMessage(messages.markAsInvalid)}
                </Wrap>
              </StyledMenuItem>
              <StyledMenuItem onClick={deleteTests}>
                <Wrap
                  fs={'1.4rem'}
                  fw={'bold'}
                  color={red}
                  padding={'.5rem 1rem'}
                >
                  {intl.formatMessage(messages.deleteSelected)}
                </Wrap>
              </StyledMenuItem>
            </Menu>
          </Wrap>
          {/*          <Column lg={25}>
            <StyledWrap>
              <StyledDatePicker />
            </StyledWrap>
          </Column>*/}
        </Wrap>
        <Wrap grow={'1'} overflow={'hidden'} width={'100%'} height={'100%'} hiddenXS>
          <StyledTable
            style={{ height: '100%' }}
            data={listTests.slice(skip, skip + pageSize)}
            onRowClick={(e) => toggleCheckTest(e.dataItem.id)}
            onPageChange={pageHandler}
            scrollable={'virtual'}
            skip={skip}
            pageSize={pageSize}
            total={total}
            rowHeight={55}
            selectedField="checked"
          >
            <GridColumn
              field="checked"
              width={'50px'}
              headerCell={() => <div />}
            />
            <GridColumn
              field="name"
              title={intl.formatMessage(messages.name)}
              width={'150px'}
            />
            <GridColumn
              field="testType"
              title={intl.formatMessage(messages.exercise)}
              width={'120px'}
            />
            <GridColumn
              field="person.firstName"
              title={intl.formatMessage(messages.firstName)}
              width={'90px'}
            />
            <GridColumn
              field="person.lastName"
              title={intl.formatMessage(messages.lastName)}
              width={'90px'}
            />
            <GridColumn
              field="person.email"
              title={intl.formatMessage(messages.email)}
            />
            <GridColumn
              field="person.phone"
              title={intl.formatMessage(messages.phone)}
              width="150px"
            />
            <GridColumn
              width={'90px'}
              field="date"
              title={intl.formatMessage(messages.date)}
              cell={(props) => (
                <td>
                  <Moment format={'MM/DD/YY'}>{props.dataItem.date}</Moment>
                </td>
              )}
            />
            <GridColumn
              width={'90px'}
              field="time"
              title={intl.formatMessage(messages.time)}
              cell={(props) => (
                <td>
                  <Moment format={'HH:mm:ss'}>{props.dataItem.date}</Moment>
                </td>
              )}
            />
            <GridColumn
              field="location"
              title={intl.formatMessage(messages.location)}
            />
            <GridColumn
              field="tester"
              title={intl.formatMessage(messages.tester)}
            />
            <GridColumn
              field="invalid"
              title={intl.formatMessage(messages.status)}
              width={'100px'}
              cell={(props) => (
                <td>
                  <Wrap justifyCont={'center'}>
                    {props.dataItem.invalid ? (
                      <Wrap
                        inlineBlock
                        padding={'.3rem .5rem'}
                        color={white}
                        br={'.5rem'}
                        bgColor={red}
                        fs={'1.2rem'}
                      >
                        {intl.formatMessage(messages.invalid)}
                      </Wrap>
                    ) : (
                      <Wrap
                        inlineBlock
                        padding={'.3rem .5rem'}
                        color={white}
                        br={'.5rem'}
                        bgColor={green}
                        fs={'1.2rem'}
                      >
                        {intl.formatMessage(messages.valid)}
                      </Wrap>
                    )}
                  </Wrap>
                </td>
              )}
            />
          </StyledTable>
        </Wrap>

        <Wrap
          width={'100%'}
          flexDir={'column'}
          overflow={'auto'}
          flexWrap={'nowrap'}
          hiddenSM
        >
          <Wrap width={'100%'}>
            {listTests.map((item, index) => (
              <Wrap
                margin={'0 0 2rem 0'}
                width={'100%'}
                padding={'3rem'}
                br={'1.2rem'}
                bgColor={white}
                key={`card-${index}`}
                flexDir={'column'}
                onClick={() => toggleCheckTest(item.id)}
              >
                <Wrap grow={'1'} justifyCont={'space-between'} width={'100%'}>
                  <Wrap flexDir={'column'} grow={'1'}>
                    <Wrap fw={'bold'} fs={'2rem'}>
                      {item.testType}
                    </Wrap>
                    <Wrap>
                      {item.person.firstName}{' '}{item.person.lastName}
                    </Wrap>
                  </Wrap>
                  <Wrap justifyCont={'center'}>
                    {item.invalid ? (
                      <Wrap
                        inlineBlock
                        padding={'.3rem .5rem'}
                        color={white}
                        br={'.5rem'}
                        bgColor={red}
                        fs={'1.2rem'}
                      >
                        {intl.formatMessage(messages.invalid)}
                      </Wrap>
                    ) : (
                      <Wrap
                        inlineBlock
                        padding={'.3rem .5rem'}
                        color={white}
                        br={'.5rem'}
                        bgColor={green}
                        fs={'1.2rem'}
                      >
                        {intl.formatMessage(messages.valid)}
                      </Wrap>
                    )}
                  </Wrap>
                </Wrap>
                <Wrap margin={'2rem 0 0 0'} flexDir={'column'} width={'100%'}>
                  <Wrap
                    color={fontColorSecond}
                    fs={'1.4rem'}
                  >
                    {item.person.email}
                  </Wrap>
                  <Wrap color={fontColorSecond} fs={'1.4rem'}>
                    {item.person.phone}
                  </Wrap>
                </Wrap>
                <Wrap
                  margin={'2rem 0'}
                  width={'100%'}
                  alignItems={'stretch'}
                  flexWrap={'nowrap'}
                >
                  <Wrap
                    flexDir={'column'}
                    grow={'1'}
                    padding={'0 1rem 0 0'}
                    borders={[{ side: 'right', color: accentColorSecond }]}
                  >
                    <Wrap
                      margin={'0 0 .5rem'}
                      color={fontColorSecond}
                      fs={'1.2rem'}
                    >
                      {intl.formatMessage(messages.date)}
                    </Wrap>
                    <Wrap fw={'bold'}>
                      <Moment format={'MM/DD/YY'}>{item.date}</Moment>
                    </Wrap>
                  </Wrap>
                  <Wrap
                    flexDir={'column'}
                    grow={'1'}
                    padding={'0 1rem'}
                    borders={[{ side: 'right', color: accentColorSecond }]}
                  >
                    <Wrap
                      margin={'0 0 .5rem'}
                      color={fontColorSecond}
                      fs={'1.2rem'}
                    >
                      {intl.formatMessage(messages.time)}
                    </Wrap>
                    <Wrap>
                      <Wrap fw={'bold'}>
                        <Moment format={'HH:mm:ss'}>{item.date}</Moment>
                      </Wrap>
                    </Wrap>
                  </Wrap>
                  <Wrap
                    flexDir={'column'}
                    grow={'1'}
                    padding={'0 0 0 1rem'}
                  >
                    <Wrap
                      margin={'0 0 .5rem'}
                      color={fontColorSecond}
                      fs={'1.2rem'}
                    >
                      {intl.formatMessage(messages.location)}
                    </Wrap>
                    <Wrap>
                      <Wrap fw={'bold'}>
                        {item.location}
                      </Wrap>
                    </Wrap>
                  </Wrap>
                </Wrap>
                <Wrap
                  width={'100%'}
                >
                  <Wrap
                    flexDir={'column'}
                    grow={'1'}
                    padding={'0 0 0 1rem'}
                  >
                    <Wrap
                      margin={'0 0 .5rem'}
                      color={fontColorSecond}
                      fs={'1.2rem'}
                    >
                      {intl.formatMessage(messages.tester)}
                    </Wrap>
                    <Wrap>
                      <Wrap fw={item.tester ? 'bold' : 'normal'} color={item.tester ? fontColorPrim : fontColorSecond}>
                        {item.tester ? item.tester : '-'}
                      </Wrap>
                    </Wrap>
                  </Wrap>
                </Wrap>
              </Wrap>
            ))}
          </Wrap>
        </Wrap>
      </Wrap>
    </>
  );
};

export default connector(SiteOwnerTestResults);
